import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { getProjects, getValuations, createValuation, deleteValuation } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, Trash2, Plus, X, Calculator, DollarSign } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const Valuation = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get('project') || '';
  
  const [projects, setProjects] = useState([]);
  const [valuations, setValuations] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentValuation, setCurrentValuation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    method: 'both',
    free_cash_flows: [],
    terminal_growth_rate: 2,
    wacc: 10,
    ebitda: 0,
    revenue: 0,
    net_income: 0,
    ev_ebitda_multiple: 8,
    ps_multiple: 2,
    pe_multiple: 15
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchValuations();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (id && valuations.length > 0) {
      const val = valuations.find(v => v.id === id);
      if (val) {
        setCurrentValuation(val);
        setFormData({
          name: val.name,
          description: val.description || '',
          method: val.method,
          ...val.inputs
        });
      }
    }
  }, [id, valuations]);

  const fetchProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (!selectedProject && response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchValuations = async () => {
    try {
      const response = await getValuations(selectedProject);
      setValuations(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSave = async () => {
    if (!selectedProject) {
      toast.error('Seleziona un progetto');
      return;
    }
    if (!formData.name) {
      toast.error('Inserisci un nome');
      return;
    }

    setSaving(true);
    try {
      const data = {
        project_id: selectedProject,
        name: formData.name,
        description: formData.description,
        method: formData.method,
        free_cash_flows: formData.free_cash_flows.map(cf => parseFloat(cf) || 0),
        terminal_growth_rate: parseFloat(formData.terminal_growth_rate),
        wacc: parseFloat(formData.wacc),
        ebitda: parseFloat(formData.ebitda),
        revenue: parseFloat(formData.revenue),
        net_income: parseFloat(formData.net_income),
        ev_ebitda_multiple: parseFloat(formData.ev_ebitda_multiple),
        ps_multiple: parseFloat(formData.ps_multiple),
        pe_multiple: parseFloat(formData.pe_multiple)
      };

      const response = await createValuation(data);
      setCurrentValuation(response.data);
      fetchValuations();
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentValuation || !window.confirm('Sei sicuro di voler eliminare questa valutazione?')) return;
    
    try {
      await deleteValuation(currentValuation.id);
      setCurrentValuation(null);
      resetForm();
      fetchValuations();
      toast.success(t('common.success'));
      navigate(`/valuation?project=${selectedProject}`);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      method: 'both',
      free_cash_flows: [],
      terminal_growth_rate: 2,
      wacc: 10,
      ebitda: 0,
      revenue: 0,
      net_income: 0,
      ev_ebitda_multiple: 8,
      ps_multiple: 2,
      pe_multiple: 15
    });
  };

  const handleNewValuation = () => {
    setCurrentValuation(null);
    resetForm();
    navigate(`/valuation?project=${selectedProject}`);
  };

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const updateFcf = (index, value) => {
    const newFcf = [...formData.free_cash_flows];
    newFcf[index] = value;
    setFormData({ ...formData, free_cash_flows: newFcf });
  };

  const addFcf = () => {
    setFormData({ ...formData, free_cash_flows: [...formData.free_cash_flows, 0] });
  };

  const removeFcf = (index) => {
    const newFcf = formData.free_cash_flows.filter((_, i) => i !== index);
    setFormData({ ...formData, free_cash_flows: newFcf });
  };

  const getChartData = () => {
    if (!currentValuation) return [];
    const data = [];
    
    if (currentValuation.dcf_value) {
      data.push({ name: 'DCF', value: currentValuation.dcf_value, color: '#6366f1' });
    }
    
    if (currentValuation.multiples_values) {
      if (currentValuation.multiples_values.ev_ebitda) {
        data.push({ name: 'EV/EBITDA', value: currentValuation.multiples_values.ev_ebitda, color: '#14b8a6' });
      }
      if (currentValuation.multiples_values.price_sales) {
        data.push({ name: 'P/Sales', value: currentValuation.multiples_values.price_sales, color: '#f59e0b' });
      }
      if (currentValuation.multiples_values.price_earnings) {
        data.push({ name: 'P/E', value: currentValuation.multiples_values.price_earnings, color: '#ec4899' });
      }
    }
    
    return data;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="valuation-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{t('val.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {currentValuation ? currentValuation.name : 'Nuova valutazione'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleNewValuation} data-testid="new-valuation-btn">
            <Plus size={16} className="mr-2" />
            Nuovo
          </Button>
          {currentValuation && (
            <Button variant="outline" onClick={handleDelete} className="text-destructive" data-testid="delete-valuation-btn">
              <Trash2 size={16} className="mr-2" />
              {t('common.delete')}
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-violet-600 to-indigo-600" data-testid="save-btn">
            <Save size={16} className="mr-2" />
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </div>

      {/* Project Selection */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Progetto</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger data-testid="project-select">
                  <SelectValue placeholder="Seleziona progetto" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} - {p.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Valutazioni Salvate</Label>
              <Select 
                value={currentValuation?.id || ''} 
                onValueChange={(value) => {
                  navigate(`/valuation/${value}?project=${selectedProject}`);
                }}
              >
                <SelectTrigger data-testid="saved-valuations-select">
                  <SelectValue placeholder="Seleziona valutazione" />
                </SelectTrigger>
                <SelectContent>
                  {valuations.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('val.method')}</Label>
              <Select value={formData.method} onValueChange={(v) => updateField('method', v)}>
                <SelectTrigger data-testid="method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dcf">{t('val.dcf')}</SelectItem>
                  <SelectItem value="multiples">{t('val.multiples')}</SelectItem>
                  <SelectItem value="both">{t('val.both')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="bg-muted/50 p-1 rounded-xl w-full sm:w-auto">
          <TabsTrigger value="general" className="rounded-lg" data-testid="tab-general">Generale</TabsTrigger>
          {(formData.method === 'dcf' || formData.method === 'both') && (
            <TabsTrigger value="dcf" className="rounded-lg" data-testid="tab-dcf">DCF</TabsTrigger>
          )}
          {(formData.method === 'multiples' || formData.method === 'both') && (
            <TabsTrigger value="multiples" className="rounded-lg" data-testid="tab-multiples">Multipli</TabsTrigger>
          )}
          <TabsTrigger value="results" className="rounded-lg" data-testid="tab-results">Risultati</TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Informazioni Generali</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('val.name')}</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="Valutazione Q4 2024"
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Descrizione opzionale"
                    data-testid="input-description"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* DCF Tab */}
        {(formData.method === 'dcf' || formData.method === 'both') && (
          <TabsContent value="dcf">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>{t('val.dcf')}</CardTitle>
                  <Button variant="outline" size="sm" onClick={addFcf} data-testid="add-fcf-btn">
                    <Plus size={16} className="mr-2" />
                    Aggiungi FCF
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('val.wacc')}</Label>
                    <Input
                      type="number"
                      value={formData.wacc}
                      onChange={(e) => updateField('wacc', e.target.value)}
                      step="0.1"
                      data-testid="input-wacc"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('val.terminalGrowth')}</Label>
                    <Input
                      type="number"
                      value={formData.terminal_growth_rate}
                      onChange={(e) => updateField('terminal_growth_rate', e.target.value)}
                      step="0.1"
                      data-testid="input-terminal-growth"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-4 block">{t('val.fcf')} (proiezione esplicita)</Label>
                  {formData.free_cash_flows.length > 0 ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      {formData.free_cash_flows.map((fcf, index) => (
                        <div key={index} className="space-y-2">
                          <Label className="flex items-center justify-between">
                            Anno {index + 1}
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6" 
                              onClick={() => removeFcf(index)}
                            >
                              <X size={14} />
                            </Button>
                          </Label>
                          <Input
                            type="number"
                            value={fcf}
                            onChange={(e) => updateFcf(index, e.target.value)}
                            data-testid={`fcf-${index}`}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-xl">
                      <p>Nessun FCF inserito</p>
                      <Button variant="outline" className="mt-4" onClick={addFcf}>
                        <Plus size={16} className="mr-2" />
                        Aggiungi FCF
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Multiples Tab */}
        {(formData.method === 'multiples' || formData.method === 'both') && (
          <TabsContent value="multiples">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('val.multiples')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-4">Dati Aziendali</h4>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>{t('val.ebitda')}</Label>
                      <Input
                        type="number"
                        value={formData.ebitda}
                        onChange={(e) => updateField('ebitda', e.target.value)}
                        data-testid="input-ebitda"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('val.revenue')}</Label>
                      <Input
                        type="number"
                        value={formData.revenue}
                        onChange={(e) => updateField('revenue', e.target.value)}
                        data-testid="input-revenue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('val.netIncome')}</Label>
                      <Input
                        type="number"
                        value={formData.net_income}
                        onChange={(e) => updateField('net_income', e.target.value)}
                        data-testid="input-net-income"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-4">Multipli di Mercato</h4>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>{t('val.evEbitda')}</Label>
                      <Input
                        type="number"
                        value={formData.ev_ebitda_multiple}
                        onChange={(e) => updateField('ev_ebitda_multiple', e.target.value)}
                        step="0.1"
                        data-testid="input-ev-ebitda"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('val.pSales')}</Label>
                      <Input
                        type="number"
                        value={formData.ps_multiple}
                        onChange={(e) => updateField('ps_multiple', e.target.value)}
                        step="0.1"
                        data-testid="input-ps"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('val.pe')}</Label>
                      <Input
                        type="number"
                        value={formData.pe_multiple}
                        onChange={(e) => updateField('pe_multiple', e.target.value)}
                        step="0.1"
                        data-testid="input-pe"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Results Tab */}
        <TabsContent value="results">
          {currentValuation ? (
            <div className="space-y-6">
              {/* Value Cards */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {currentValuation.dcf_value && (
                  <Card className="border-0 shadow-lg bg-gradient-to-br from-violet-500 to-indigo-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-white/80">{t('val.dcfValue')}</span>
                        <Calculator size={20} />
                      </div>
                      <p className="text-2xl font-bold">
                        €{currentValuation.dcf_value?.toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {currentValuation.multiples_values?.ev_ebitda && (
                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">EV/EBITDA</span>
                        <Badge variant="secondary">{formData.ev_ebitda_multiple}x</Badge>
                      </div>
                      <p className="text-2xl font-bold">
                        €{currentValuation.multiples_values.ev_ebitda?.toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {currentValuation.multiples_values?.price_sales && (
                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">P/Sales</span>
                        <Badge variant="secondary">{formData.ps_multiple}x</Badge>
                      </div>
                      <p className="text-2xl font-bold">
                        €{currentValuation.multiples_values.price_sales?.toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {currentValuation.multiples_values?.price_earnings && (
                  <Card className="border-0 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">P/E</span>
                        <Badge variant="secondary">{formData.pe_multiple}x</Badge>
                      </div>
                      <p className="text-2xl font-bold">
                        €{currentValuation.multiples_values.price_earnings?.toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Average Value */}
              {currentValuation.average_value && (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-8 text-center">
                    <DollarSign size={48} className="mx-auto mb-4 text-primary" />
                    <p className="text-muted-foreground mb-2">{t('val.averageValue')}</p>
                    <p className="text-4xl font-bold gradient-text">
                      €{currentValuation.average_value?.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Chart */}
              {getChartData().length > 1 && (
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle>Confronto Metodi di Valutazione</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getChartData()}
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                            dataKey="value"
                            label={({ name, value }) => `${name}: €${(value/1000000).toFixed(1)}M`}
                          >
                            {getChartData().map((entry, index) => (
                              <Cell key={index} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `€${value.toLocaleString()}`} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card className="border-0 shadow-lg">
              <CardContent className="py-12 text-center text-muted-foreground">
                <Calculator size={48} className="mx-auto mb-4 opacity-30" />
                <p>Salva la valutazione per visualizzare i risultati</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Valuation;
